/**
 * Shared theme registry for the web app.
 *
 * Single source of truth for the 13 themes used both inside the app
 * (ThemeContext, theme picker menu, anti-flash boot script) and on the
 * marketing landing page (where every theme is rendered as a tile).
 *
 * Ported from `ios/zubi/Theme.swift` — the iOS app and the web app must
 * stay aligned on palette, naming, and order. The `landing/themeData.ts`
 * file re-exports from here under its old names so existing landing-page
 * imports continue to work without source changes.
 *
 * Order matches `ThemeDefinition.all` in Theme.swift (line 149) so the
 * visual flow on the landing page mirrors the in-app theme picker.
 */

export interface ThemeDefinition {
  /** camelCase id, matching Swift `ThemeDefinition.id`. */
  id: string;
  /** Display name shown in the picker / tile caption (e.g. "Solarized Dark"). */
  name: string;
  /** Whether the theme is intrinsically light or dark. */
  scheme: 'light' | 'dark';
  background: string;
  foreground: string;
  mutedForeground: string;
  secondary: string;
  border: string;
  /** Color used for the note title text in editor previews. */
  noteTitleColor: string;
  /** Whether the theme is gated behind a premium subscription. */
  isPremium: boolean;
}

export const themes: ThemeDefinition[] = [
  {
    id: 'light',
    name: 'Light',
    scheme: 'light',
    background: '#ffffff',
    foreground: '#1a1a2e',
    mutedForeground: '#6b7280',
    secondary: '#f8f8fa',
    border: '#d1d5db',
    noteTitleColor: '#0f172a',
    isPremium: false,
  },
  {
    id: 'dark',
    name: 'Dark',
    scheme: 'dark',
    background: '#0a0e21',
    foreground: '#e1e7ef',
    mutedForeground: '#9ca3af',
    secondary: '#1f2937',
    border: '#1e293b',
    noteTitleColor: '#f8fafc',
    isPremium: false,
  },
  {
    id: 'solarizedLight',
    name: 'Solarized Light',
    scheme: 'light',
    background: '#fdf6e3',
    foreground: '#657b83',
    mutedForeground: '#93a1a1',
    secondary: '#eee8d5',
    border: '#d5cec4',
    noteTitleColor: '#073642',
    isPremium: true,
  },
  {
    id: 'solarizedDark',
    name: 'Solarized Dark',
    scheme: 'dark',
    background: '#002b36',
    foreground: '#839496',
    mutedForeground: '#657b83',
    secondary: '#073642',
    border: '#094050',
    noteTitleColor: '#fdf6e3',
    isPremium: true,
  },
  {
    id: 'gruvbox',
    name: 'Gruvbox',
    scheme: 'dark',
    background: '#282828',
    foreground: '#ebdbb2',
    mutedForeground: '#a89984',
    secondary: '#3c3836',
    border: '#504945',
    noteTitleColor: '#fbf1c7',
    isPremium: true,
  },
  {
    id: 'monokai',
    name: 'Monokai',
    scheme: 'dark',
    background: '#272822',
    foreground: '#f8f8f2',
    mutedForeground: '#8f908a',
    secondary: '#3e3d32',
    border: '#49483e',
    noteTitleColor: '#f8f8f2',
    isPremium: true,
  },
  {
    id: 'rosePine',
    name: 'Rosé Pine',
    scheme: 'dark',
    background: '#191724',
    foreground: '#e0def4',
    mutedForeground: '#6e6a86',
    secondary: '#1f1d2e',
    border: '#26233a',
    noteTitleColor: '#e0def4',
    isPremium: true,
  },
  {
    id: 'midnight',
    name: 'Midnight',
    scheme: 'dark',
    background: '#000000',
    foreground: '#e4e4e7',
    mutedForeground: '#71717a',
    secondary: '#18181b',
    border: '#27272a',
    noteTitleColor: '#fafafa',
    isPremium: true,
  },
  {
    id: 'ocean',
    name: 'Ocean',
    scheme: 'dark',
    background: '#0d1b2a',
    foreground: '#c9d6df',
    mutedForeground: '#6b8299',
    secondary: '#1b2838',
    border: '#243447',
    noteTitleColor: '#e2e8f0',
    isPremium: true,
  },
  {
    id: 'forest',
    name: 'Forest',
    scheme: 'dark',
    background: '#1a2e1a',
    foreground: '#c8d6c0',
    mutedForeground: '#7a9370',
    secondary: '#243824',
    border: '#2e4a2e',
    noteTitleColor: '#dcebd4',
    isPremium: true,
  },
  {
    id: 'ember',
    name: 'Ember',
    scheme: 'dark',
    background: '#1f1315',
    foreground: '#e8d0d4',
    mutedForeground: '#9a7a80',
    secondary: '#2d1f22',
    border: '#3d2c30',
    noteTitleColor: '#f0e0e4',
    isPremium: true,
  },
  {
    id: 'sakura',
    name: 'Sakura',
    scheme: 'light',
    background: '#fef0f5',
    foreground: '#4a2d3a',
    mutedForeground: '#9e7a8a',
    secondary: '#f8e4ec',
    border: '#e8d0da',
    noteTitleColor: '#3a1d2a',
    isPremium: true,
  },
  {
    id: 'lavender',
    name: 'Lavender',
    scheme: 'light',
    background: '#f3f0ff',
    foreground: '#2d2a4a',
    mutedForeground: '#7a78a0',
    secondary: '#e8e4f8',
    border: '#d0cce8',
    noteTitleColor: '#1d1a3a',
    isPremium: true,
  },
];

/**
 * String-literal union of every theme id in the registry. Use this as the
 * type for any value that should be a valid theme id (e.g. ThemeContext
 * state, persisted user preference).
 */
export type ThemeId =
  | 'light'
  | 'dark'
  | 'solarizedLight'
  | 'solarizedDark'
  | 'gruvbox'
  | 'monokai'
  | 'rosePine'
  | 'midnight'
  | 'ocean'
  | 'forest'
  | 'ember'
  | 'sakura'
  | 'lavender';

/**
 * Look up a theme definition by id. Returns `undefined` if no theme with
 * that id exists in the registry — callers can decide how to fall back
 * (typically to `dark`).
 */
export function findTheme(id: string): ThemeDefinition | undefined {
  return themes.find((t) => t.id === id);
}
